import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 8 — TanStack Query hooks for the Presentation
 * workbench. `request()` throws a structured error (`.status`, `.body`)
 * so callers (via useAutosaveEntity) can distinguish a 409 stale-revision
 * conflict from any other failure.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });
const jsonPatch = (path, payload) => request(path, { method: "PATCH", body: JSON.stringify(payload) });
const jsonDelete = (path) => request(path, { method: "DELETE" });

export const qk = {
  list: (wrkId, includeArchived) => ["presentations", wrkId, includeArchived],
  detail: (wrkId, presId) => ["presentation", wrkId, presId],
  ideaCards: (wrkId, presId, includeDeleted) => ["presentation-idea-cards", wrkId, presId, includeDeleted],
  structureVersions: (wrkId, presId) => ["presentation-structure-versions", wrkId, presId],
  sections: (wrkId, presId, svId) => ["presentation-sections", wrkId, presId, svId],
  beats: (wrkId, presId, sectionId) => ["presentation-beats", wrkId, presId, sectionId],
  annotationsByLayer: (wrkId, presId, layerKey) => ["presentation-annotations-layer", wrkId, presId, layerKey],
  assetLinks: (wrkId, presId) => ["presentation-asset-links", wrkId, presId],
  versions: (wrkId, presId) => ["presentation-versions", wrkId, presId],
};

// -- Queries ------------------------------------------------------------
export function usePresentationsList(workspaceId, includeArchived = false) {
  return useQuery({
    queryKey: qk.list(workspaceId, includeArchived),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/presentations?include_archived=${includeArchived}`),
  });
}

export function usePresentationDetail(workspaceId, presId) {
  return useQuery({
    queryKey: qk.detail(workspaceId, presId),
    enabled: !!workspaceId && !!presId,
    retry: false,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/presentations/${presId}`),
  });
}

export function useIdeaCards(workspaceId, presId, includeDeleted = false) {
  return useQuery({
    queryKey: qk.ideaCards(workspaceId, presId, includeDeleted),
    enabled: !!workspaceId && !!presId,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/idea-cards?wrk_id=${workspaceId}&include_deleted=${includeDeleted}`),
  });
}

export function useStructureVersions(workspaceId, presId) {
  return useQuery({
    queryKey: qk.structureVersions(workspaceId, presId),
    enabled: !!workspaceId && !!presId,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/structure-versions?wrk_id=${workspaceId}`),
  });
}

export function useSections(workspaceId, presId, svId) {
  return useQuery({
    queryKey: qk.sections(workspaceId, presId, svId),
    enabled: !!workspaceId && !!presId && !!svId,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/sections?wrk_id=${workspaceId}&sv_id=${svId}`),
  });
}

export function useBeats(workspaceId, presId, sectionId) {
  return useQuery({
    queryKey: qk.beats(workspaceId, presId, sectionId),
    enabled: !!workspaceId && !!presId && !!sectionId,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/sections/${sectionId}/beats?wrk_id=${workspaceId}`),
  });
}

export function useAnnotationsByLayer(workspaceId, presId, layerKey) {
  return useQuery({
    queryKey: qk.annotationsByLayer(workspaceId, presId, layerKey),
    enabled: !!workspaceId && !!presId && !!layerKey,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/delivery-layers/${layerKey}?wrk_id=${workspaceId}`),
  });
}

export function useAssetLinks(workspaceId, presId) {
  return useQuery({
    queryKey: qk.assetLinks(workspaceId, presId),
    enabled: !!workspaceId && !!presId,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/asset-links?wrk_id=${workspaceId}`),
  });
}

export function useVersions(workspaceId, presId) {
  return useQuery({
    queryKey: qk.versions(workspaceId, presId),
    enabled: !!workspaceId && !!presId,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/versions?wrk_id=${workspaceId}`),
  });
}

export function useVersionCompare(workspaceId, presId, fromId, toId) {
  return useQuery({
    queryKey: ["presentation-version-compare", workspaceId, presId, fromId, toId],
    enabled: !!workspaceId && !!presId && !!fromId && !!toId,
    queryFn: () => request(`/api/rehearsal-room/presentations/${presId}/versions/compare?wrk_id=${workspaceId}&from_version_id=${fromId}&to_version_id=${toId}`),
  });
}

// -- Mutations ------------------------------------------------------------
export function useCreatePresentation(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/presentations`, { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentations", workspaceId] }),
  });
}

export function useRenamePresentation(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPatch(`/api/rehearsal-room/workspaces/${workspaceId}/presentations/${presId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.detail(workspaceId, presId) }),
  });
}

export function useArchivePresentation(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (presId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/presentations/${presId}/archive`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentations", workspaceId] }),
  });
}

export function useUnarchivePresentation(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (presId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/presentations/${presId}/unarchive`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentations", workspaceId] }),
  });
}

// Prompt 30D — permanent delete, only ever offered once a Presentation
// is already archived (see presentation_routes.delete_presentation).
export function useDeletePresentation(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (presId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/presentations/${presId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentations", workspaceId] }),
  });
}

export function useUpdateContext(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPatch(`/api/rehearsal-room/presentations/${presId}/context?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.detail(workspaceId, presId) }),
  });
}

export function useCreateIdeaCard(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/presentations/${presId}/idea-cards?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation-idea-cards", workspaceId, presId] }),
  });
}

export function useUpdateIdeaCard(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ideaId, ...payload }) => jsonPatch(`/api/rehearsal-room/presentations/${presId}/idea-cards/${ideaId}?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation-idea-cards", workspaceId, presId] }),
  });
}

export function useDeleteIdeaCard(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ideaId) => jsonPost(`/api/rehearsal-room/presentations/${presId}/idea-cards/${ideaId}/delete?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation-idea-cards", workspaceId, presId] }),
  });
}

export function useRestoreIdeaCard(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ideaId) => jsonPost(`/api/rehearsal-room/presentations/${presId}/idea-cards/${ideaId}/restore?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation-idea-cards", workspaceId, presId] }),
  });
}

export function useCreateStructureVersion(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label) => jsonPost(`/api/rehearsal-room/presentations/${presId}/structure-versions?wrk_id=${workspaceId}`, { label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.structureVersions(workspaceId, presId) }),
  });
}

export function useActivateStructureVersion(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (svId) => jsonPost(`/api/rehearsal-room/presentations/${presId}/structure-versions/${svId}/activate?wrk_id=${workspaceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.structureVersions(workspaceId, presId) });
      qc.invalidateQueries({ queryKey: qk.detail(workspaceId, presId) });
      qc.invalidateQueries({ queryKey: qk.versions(workspaceId, presId) });
    },
  });
}

export function useCreateSection(workspaceId, presId, svId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title) => jsonPost(`/api/rehearsal-room/presentations/${presId}/sections?wrk_id=${workspaceId}`, { sv_id: svId, title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sections(workspaceId, presId, svId) }),
  });
}

export function useUpdateSection(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...payload }) => jsonPatch(`/api/rehearsal-room/presentations/${presId}/sections/${sectionId}?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation-sections", workspaceId, presId] }),
  });
}

export function useDeleteSection(workspaceId, presId, svId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId) => jsonDelete(`/api/rehearsal-room/presentations/${presId}/sections/${sectionId}?wrk_id=${workspaceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.sections(workspaceId, presId, svId) });
      qc.invalidateQueries({ queryKey: qk.versions(workspaceId, presId) });
    },
  });
}

export function useCreateBeat(workspaceId, presId, sectionId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/presentations/${presId}/sections/${sectionId}/beats?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.beats(workspaceId, presId, sectionId) }),
  });
}

export function useUpdateBeat(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ beatId, ...payload }) => jsonPatch(`/api/rehearsal-room/presentations/${presId}/beats/${beatId}?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation-beats", workspaceId, presId] }),
  });
}

export function useDeleteBeat(workspaceId, presId, sectionId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (beatId) => jsonDelete(`/api/rehearsal-room/presentations/${presId}/beats/${beatId}?wrk_id=${workspaceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.beats(workspaceId, presId, sectionId) });
      qc.invalidateQueries({ queryKey: qk.versions(workspaceId, presId) });
    },
  });
}

export function useCreateAnnotation(workspaceId, presId, beatId, layerKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/presentations/${presId}/beats/${beatId}/annotations?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.annotationsByLayer(workspaceId, presId, layerKey) }),
  });
}

export function useUpdateAnnotation(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ danId, ...payload }) => jsonPatch(`/api/rehearsal-room/presentations/${presId}/annotations/${danId}?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation-annotations-layer", workspaceId, presId] }),
  });
}

export function useCreateAssetLink(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/presentations/${presId}/asset-links?wrk_id=${workspaceId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.assetLinks(workspaceId, presId) }),
  });
}

export function useDeleteAssetLink(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (palId) => jsonDelete(`/api/rehearsal-room/presentations/${presId}/asset-links/${palId}?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.assetLinks(workspaceId, presId) }),
  });
}

export function useSaveVersion(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label) => jsonPost(`/api/rehearsal-room/presentations/${presId}/versions?wrk_id=${workspaceId}`, { label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.versions(workspaceId, presId) }),
  });
}

export function useRestoreVersion(workspaceId, presId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId) => jsonPost(`/api/rehearsal-room/presentations/${presId}/versions/${versionId}/restore?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("presentation") }),
  });
}
